import { OrganizationSortValue } from "@common/types/organization-admin";
import { FLAT_RATE_PLANS, PLAN_CATALOG_VERSION } from "@common/flatRateBilling";
import {
    DEFAULT_DEMO_FIXTURE_ENVIRONMENT,
    DEMO_FIXTURE_ENVIRONMENTS,
} from "@common/constants/demo";
import { z } from "zod";

export const OrganizationAdminGetOrganizationsBodySchema = z.object({
    search: z.string().optional(),
    page: z.number().optional(),
    limit: z.number().optional(),
    sort: z.nativeEnum(OrganizationSortValue).optional(),
    excludeDemo: z.boolean().optional(),
});

export const OrganizationAdminUpsertBillingPathParamsSchema = z.object({
    organizationId: z.string().uuid(),
});

export const OrganizationAdminUpsertBillingBodySchema = z.object({
    subscriptionId: z.string().min(1),
});

export const OrganizationAdminLinkStripeCustomerPathParamsSchema = z.object({
    organizationId: z.string().uuid(),
});

export const OrganizationAdminLinkStripeCustomerBodySchema = z.object({
    stripeCustomerId: z.string().min(1),
});

const OrganizationAdminBillingFieldChangeSchema = z.object({
    from: z.string().nullable(),
    to: z.string().nullable(),
});

export const OrganizationAdminLinkStripeCustomerResponseSchema = z.object({
    action: z.enum(["created", "updated"]),
    billingId: z.string().uuid(),
    stripeCustomerId: z.string(),
    setupIntentId: z.string().nullable(),
    updatedFields: z.object({
        stripeCustomerId: OrganizationAdminBillingFieldChangeSchema.optional(),
        setupIntentId: OrganizationAdminBillingFieldChangeSchema.optional(),
    }),
});

export const OrganizationAdminSetupV3SubscriptionPathParamsSchema = z.object({
    organizationId: z.string().uuid(),
});

export const OrganizationAdminAipPathParamsSchema = z.object({
    organizationId: z.string().uuid(),
});

export const OrganizationAdminAipBodySchema = z.object({
    creditAmountDollars: z
        .number()
        .int()
        .nonnegative()
        .optional()
        .default(6000),
    aipProgramSince: z.string().date().optional(),
});

export const OrganizationAdminAllowedPlansPathParamsSchema = z.object({
    organizationId: z.string().uuid(),
});

export const OrganizationAdminAllowedPlansBodySchema = z.object({
    allowedPlans: z
        .array(z.nativeEnum(FLAT_RATE_PLANS))
        .max(Object.keys(FLAT_RATE_PLANS).length)
        .transform((plans) => [...new Set(plans)].sort()),
});

export const OrganizationAdminBillingTransferDiscountCodePathParamsSchema =
    z.object({
        organizationId: z.string().uuid(),
    });

export const OrganizationAdminBillingTransferDiscountCodeBodySchema = z.object({
    billingTransferDiscountCode: z.string().trim().min(1).max(100).nullable(),
});

export const OrganizationAdminDeletePathParamsSchema = z.object({
    organizationId: z.string().uuid(),
});

export const OrganizationAdminDeleteResponseSchema = z.object({
    deletedWorkspaceIds: z.array(z.string().uuid()),
    stripeSubscriptionId: z.string().nullable(),
    wasStripeSubscriptionCancelled: z.boolean(),
});

export const OrganizationAdminCancelSubscriptionPathParamsSchema = z.object({
    organizationId: z.string().uuid(),
});

export const OrganizationAdminCreateDemoWorkspacePathParamsSchema = z.object({
    organizationId: z.string().uuid(),
});

export const OrganizationAdminCreateDemoWorkspaceBodySchema = z.object({
    name: z.string().trim().min(1).max(255),
    environment: z
        .enum(DEMO_FIXTURE_ENVIRONMENTS)
        .optional()
        .default(DEFAULT_DEMO_FIXTURE_ENVIRONMENT),
});

export const OrganizationAdminCancelSubscriptionResponseSchema = z.object({
    endDatedWorkspaceIds: z.array(z.string().uuid()),
    wasStripeSubscriptionCancelled: z.boolean(),
    wasStripeRefunded: z.boolean(),
});

export const OrganizationAdminDropSubscriptionLinkPathParamsSchema = z.object({
    organizationId: z.string().uuid(),
});

export const OrganizationAdminDropSubscriptionLinkResponseSchema = z.object({
    previousSubscriptionId: z.string(),
    previousSubscriptionStatus: z.string().nullable(),
});

export const OrganizationAdminMoveToV3CatalogPathParamsSchema = z.object({
    organizationId: z.string().uuid(),
});

export const OrganizationAdminMoveToV3CatalogResponseSchema = z.object({
    fromCatalogVersion: z.nativeEnum(PLAN_CATALOG_VERSION),
    toCatalogVersion: z.nativeEnum(PLAN_CATALOG_VERSION),
    stripeCustomerId: z.string(),
    createdCustomer: z.boolean(),
});

export const OrganizationAdminRetriggerBillingReportPathParamsSchema = z.object(
    {
        organizationId: z.string().uuid(),
    },
);

export const OrganizationAdminRetriggerBillingReportResponseSchema = z.object({
    subscriptionId: z.string(),
    added: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
    deleted: z.number().int().nonnegative(),
    noop: z.boolean(),
});

export const OrganizationAdminQuantitiesExplainPathParamsSchema = z.object({
    organizationId: z.string().uuid(),
});

export const OrganizationAdminQuantitiesExplainQuerySchema = z.object({
    mode: z.enum(["active_v3", "enrollment_candidate"]).optional(),
});

const OrganizationAdminQuantitiesExplainEntitySchema = z.object({
    entityId: z.number().int().positive(),
    name: z.string().min(1),
    role: z.enum([
        "paid_additional",
        "included_in_license",
        "below_threshold",
        "excluded",
    ]),
});

export const OrganizationAdminQuantitiesExplainExclusionReasonSchema = z.enum([
    "org_demo",
    "demo",
    "client_billed",
    "other",
]);

const OrganizationAdminQuantitiesExplainWorkspaceBaseSchema = z.object({
    workspaceId: z.string().uuid(),
    workspaceName: z.string().min(1),
    plan: z.string().nullable(),
    billingPriceId: z.string().uuid().nullable(),
    additionalEntityBillingPriceId: z.string().uuid().nullable(),
    additionalEntityCount: z.number().int().nonnegative(),
    /** Stripe price the client seat is counted under, after tiers. */
    resolvedStripePriceId: z.string().nullable(),
    /** Stripe price of an explicit per-workspace additional-entity price. */
    resolvedAdditionalEntityStripePriceId: z.string().nullable(),
    reason: z.string(),
    entities: z.array(OrganizationAdminQuantitiesExplainEntitySchema),
});

/**
 * Explain-only attribution of complimentary pool seats to specific
 * workspaces (stable by workspaceId). Does not change Stripe quantities.
 */
const OrganizationAdminQuantitiesExplainCountedWorkspaceSchema =
    OrganizationAdminQuantitiesExplainWorkspaceBaseSchema.extend({
        licenseStatus: z.enum([
            "complimentary",
            "billed",
            "custom",
            "not_billed",
        ]),
        exclusionReason: z.null(),
    });

/** Assigned to the org but skipped by the billing calculation. */
const OrganizationAdminQuantitiesExplainExcludedWorkspaceSchema =
    OrganizationAdminQuantitiesExplainWorkspaceBaseSchema.extend({
        licenseStatus: z.literal("excluded"),
        exclusionReason:
            OrganizationAdminQuantitiesExplainExclusionReasonSchema,
    });

const OrganizationAdminQuantitiesExplainWorkspaceSchema = z.discriminatedUnion(
    "licenseStatus",
    [
        OrganizationAdminQuantitiesExplainCountedWorkspaceSchema,
        OrganizationAdminQuantitiesExplainExcludedWorkspaceSchema,
    ],
);

export const OrganizationAdminQuantitiesExplainResponseSchema = z.object({
    organizationId: z.string().uuid(),
    policyRevisionId: z.string().uuid().nullable(),
    mode: z.enum(["active_v3", "enrollment_candidate"]),
    clientPrices: z.array(
        z.object({ stripePriceId: z.string(), quantity: z.number().int() }),
    ),
    additionalEntities: z.array(
        z.object({ stripePriceId: z.string(), quantity: z.number().int() }),
    ),
    complimentaryBasicCount: z.number().int().nonnegative(),
    complimentaryPlusCount: z.number().int().nonnegative(),
    complimentaryAdvancedCount: z.number().int().nonnegative(),
    complimentaryAdditionalEntityCount: z.number().int().nonnegative(),
    complimentaryReadOnlyCount: z.number().int().nonnegative(),
    complimentaryTaxCount: z.number().int().nonnegative(),
    /** Set when the org has no billing snapshot; quantities are then zero. */
    warning: z.string().nullable(),
    workspaces: z.array(OrganizationAdminQuantitiesExplainWorkspaceSchema),
});

export type OrganizationAdminQuantitiesExplainWorkspace = z.infer<
    typeof OrganizationAdminQuantitiesExplainWorkspaceSchema
>;

export type OrganizationAdminQuantitiesExplainCountedWorkspace = z.infer<
    typeof OrganizationAdminQuantitiesExplainCountedWorkspaceSchema
>;

export type OrganizationAdminQuantitiesExplainExcludedWorkspace = z.infer<
    typeof OrganizationAdminQuantitiesExplainExcludedWorkspaceSchema
>;

export type OrganizationAdminQuantitiesExplainExclusionReason = z.infer<
    typeof OrganizationAdminQuantitiesExplainExclusionReasonSchema
>;

export type OrganizationAdminQuantitiesExplainEntity = z.infer<
    typeof OrganizationAdminQuantitiesExplainEntitySchema
>;

export type OrganizationAdminQuantitiesExplainResponse = z.infer<
    typeof OrganizationAdminQuantitiesExplainResponseSchema
>;
